import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { GlobalRole, UserSummary } from '../api/types';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', initialPassword: '', globalRole: 'USER' as GlobalRole });
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    api.get<UserSummary[]>('/users').then(setUsers);
  };

  useEffect(load, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/users', form);
      setForm({ email: '', firstName: '', lastName: '', initialPassword: '', globalRole: 'USER' });
      setShowCreate(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const toggleStatus = async (u: UserSummary) => {
    await api.patch(`/users/${u.id}/${u.status === 'ACTIVE' ? 'disable' : 'enable'}`);
    load();
  };

  const changeRole = async (u: UserSummary, globalRole: GlobalRole) => {
    await api.patch(`/users/${u.id}`, { globalRole });
    load();
  };

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('users.title')}</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate((v) => !v)}>
          {t('users.newUser')}
        </button>
      </div>

      {showCreate && (
        <form className="card" style={{ marginBottom: 20, maxWidth: 420 }} onSubmit={handleCreate}>
          <div className="form-field">
            <label>{t('users.email')}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-field">
            <label>{t('users.firstName')}</label>
            <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div className="form-field">
            <label>{t('users.lastName')}</label>
            <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <div className="form-field">
            <label>{t('users.initialPassword')}</label>
            <input type="password" required minLength={10} value={form.initialPassword} onChange={(e) => setForm({ ...form, initialPassword: e.target.value })} />
          </div>
          <div className="form-field">
            <label>{t('users.role')}</label>
            <select value={form.globalRole} onChange={(e) => setForm({ ...form, globalRole: e.target.value as GlobalRole })}>
              <option value="USER">{t('users.roleUser')}</option>
              <option value="SUPER_USER">{t('users.roleSuperUser')}</option>
              <option value="ADMIN">{t('users.roleAdmin')}</option>
            </select>
          </div>
          {error && <p className="error-text">{error}</p>}
          <button type="submit" className="btn btn-primary">
            {t('users.create')}
          </button>
        </form>
      )}

      <table>
        <thead>
          <tr>
            <th>{t('users.email')}</th>
            <th>{t('users.role')}</th>
            <th>{t('users.provider')}</th>
            <th>{t('users.status')}</th>
            <th>{t('users.lastLogin')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>
                {u.firstName} {u.lastName}
                <br />
                <span className="badge">{u.email}</span>
              </td>
              <td>
                <select value={u.globalRole} onChange={(e) => changeRole(u, e.target.value as GlobalRole)}>
                  <option value="USER">{t('users.roleUser')}</option>
                  <option value="SUPER_USER">{t('users.roleSuperUser')}</option>
                  <option value="ADMIN">{t('users.roleAdmin')}</option>
                </select>
              </td>
              <td>{u.authProvider}</td>
              <td>{u.status === 'ACTIVE' ? t('users.active') : t('users.disabled')}</td>
              <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : t('users.never')}</td>
              <td>
                <button className="btn" onClick={() => toggleStatus(u)}>
                  {u.status === 'ACTIVE' ? t('users.disable') : t('users.enable')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
